import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { FeatureFlagService, PRICING_ACTION_LOOP_FLAG_KEY } from './feature-flag.service';
import {
  PricingActionEnablementService,
  TenantEntitlement,
} from '../tenant/pricing-action-enablement.service';
import { PricingActionDenialKind, denyToHome } from '../router/pricing-action-denial';

const DENIAL_FOR_ENTITLEMENT: Readonly<
  Record<Exclude<TenantEntitlement, 'entitled'>, PricingActionDenialKind>
> = {
  'not-entitled': 'tenant-not-entitled',
  'lookup-failed': 'tenant-check-failed',
  // The bootstrap this guard awaits reads the caller's profile too, so a rejected `/me` is met
  // here and the child permission guard never runs. It is one reader's access that is unknown,
  // not the account's, so the message has to be the permission one.
  'profile-read-failed': 'permission-check-failed',
};

/**
 * Gates the `/pricing-action` subtree on the rollout flag, then on the active tenant's
 * entitlement. The flag falls back to `environment.features.pricingActionLoop` when Amplitude
 * is disabled or holds no variant for the caller — each environment file sets its own value.
 * A deny goes to Home carrying why, and tells a read that FAILED (SDK never initialised, variant
 * fetch rejected, profile or tenant lookup rejected) apart from one that resolved to off, so Home
 * offers Retry only where a retry can change the answer.
 */
export const pricingActionLoopEnabledGuard: CanActivateFn = async (_route, state) => {
  const flags = inject(FeatureFlagService);
  const router = inject(Router);
  const enablement = inject(PricingActionEnablementService);
  const deny = (kind: PricingActionDenialKind) =>
    denyToHome(router, { kind, attemptedUrl: state.url });

  await flags.ready();
  const rolloutOn = flags.isEnabledWithFallback(
    PRICING_ACTION_LOOP_FLAG_KEY,
    environment.features.pricingActionLoop
  );
  if (!rolloutOn) {
    return deny(flags.variantsResolved() ? 'rollout-off' : 'rollout-unresolved');
  }
  const entitlement = await enablement.entitlementForCurrentTenant();
  return entitlement === 'entitled' ? true : deny(DENIAL_FOR_ENTITLEMENT[entitlement]);
};
